import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../../auth';

/**
 * Acts the same as useState, but is backed by localStorage so the value persists across sessions
 * Note: the value is stored by user, so on shared devices it does not affect others' sessions
 *
 * The value lives in React state rather than being read back out of storage on
 * every write (see `setStorageValue`), so two instances mounted on the same key
 * no longer stay in sync through localStorage — mount one per key.
 */
export function useStorage<T>(key: string, defaultValue: T) {
  const { auth } = useAuth();
  if (!auth) {
    throw new Error('Cannot use local storage without authentication');
  }

  const userKey = `${auth.userId}:${key}`;

  const [value, setValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(userKey);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      return defaultValue;
    }
  });

  // Mirrors `value` so the functional form can compose without the setter being
  // a state updater. Persistence has to stay out of the updater: React may
  // replay a queued update, which would run the write again for a render it
  // discards. It also has to stay synchronous, so a second setter call in the
  // same tick composes on top of this one rather than both reading the same
  // stale value. The setter is the only writer, so the ref can't drift.
  //
  // Derived from React state, not localStorage: once a write fails the two
  // diverge, and re-reading storage would compute the next value from a stale
  // one — leaving a toggle that defaults to `false` stuck on after its first
  // click, since every later toggle would recompute the same `true`.
  const valueRef = useRef(value);

  const setStorageValue = useCallback(
    (newValueOrFn: T | ((prevValue: T) => T)) => {
      const newValue =
        typeof newValueOrFn === 'function'
          ? (newValueOrFn as (prevValue: T) => T)(valueRef.current)
          : newValueOrFn;

      // Updated before the write so the next call in this tick sees it.
      valueRef.current = newValue;

      try {
        localStorage.setItem(userKey, JSON.stringify(newValue));
      } catch (error) {
        // Storage can be blocked or full (quota, Safari private mode). The
        // in-memory value still has to move: the Archive section defaults to
        // collapsed, so a failed write would otherwise make it impossible to
        // open — with no way to recover.
        console.error('Error saving to localStorage:', error);
      }

      setValue(newValue);
    },
    [userKey],
  );

  return [value, setStorageValue] as const;
}
